import axios, { AxiosRequestConfig } from 'axios';

import { DEFAULT_API_CONFIG, TENANT_ID } from './constants';

export const setAccessToken = (token?: string) => {
  AXIOS.defaults.headers.common['Authorization'] = token ? `Bearer ${token}` : token;
};

const AXIOS = axios.create(DEFAULT_API_CONFIG);

export const TenantApiUrl = DEFAULT_API_CONFIG.baseURL + `tenant/${TENANT_ID}`;

const get = async (url: string, config?: AxiosRequestConfig) => {
  return AXIOS.get(`tenant/${TENANT_ID}/${url}`, config);
};

const post = async (url: string, data?: any, config?: AxiosRequestConfig) => {
  return AXIOS.post(`tenant/${TENANT_ID}/${url}`, data, config);
};

const put = async (url: string, data?: any, config?: AxiosRequestConfig) => {
  return AXIOS.put(`tenant/${TENANT_ID}/${url}`, data, config);
};

const del = async (url: string) => {
  return AXIOS.delete(`tenant/${TENANT_ID}/${url}`);
};

// Common routes
export const getPlatformData = async () => {
  return await get(`common/platformData`);
};

export const getZxcvbnOptions = async () => {
  return await get(`common/auth/options`);
};

export const loginBasic = async (data: LoginData) => {
  return await post(`common/auth/login`, data);
};

export const loginSSO = async (data: any) => {
  return await post(`common/auth/sso`, data);
};

export const loginSSO2 = async (data: { email: string }) => {
  return await post(`common/auth/sso2`, data);
};

export const loginGoogle = async () => {
  return await get(`common/auth/google`);
};

export const logout = async () => {
  return await get(`common/auth/logout`, { maxRedirects: 0 });
};

export const signupUser = async (user: IUser) => {
  return await post(`common/users/signup`, user);
};

export const getUserWithToken = async (userId: string, token: string) => {
  return await get(`common/users/${userId}/token/${token}`);
};

export const registerUser = async (user: IUser, token: string) => {
  return await post(`common/users/${user._id}/register?token=${token}`, user);
};

export const forgotPassword = async (data: { email: string; token: string }) => {
  return await post(`common/users/forgot`, data);
};

export const resetPassword = async (data: {
  password: string;
  resetToken: string;
  token: string;
}) => {
  return await post(`common/users/reset`, data);
};

export const getIsSSOUser = async (userId: string) => {
  return await get(`common/auth/${userId}`);
};

/** Authed routes */
export const checkAuthed = async () => {
  return await get(`authed/auth`);
};

export const getWSToken = async () => {
  return await get(`authed/auth/ws`);
};

export const updateSelf = async (formData: FormData) => {
  return await put(`authed/users/self`, formData);
};

export const signupAuthedUser = async (code: string) => {
  return await post(`authed/users/signup`, { code });
};

export const getLearnerCourse = async (courseId: string) => {
  return await get(`authed/learner/courses/${courseId}`);
};

export const getLearnerCourseProgress = async (courseId: string) => {
  return await get(`authed/learner/courses/${courseId}/progress`);
};

export const getLearnerCertificate = async (courseId: string) => {
  return await get(`authed/learner/courses/${courseId}/certificate`, {
    responseType: 'blob',
  });
};

export const getLearnerModule = async ({
  courseId,
  moduleId,
}: {
  courseId: string;
  moduleId: string;
}) => {
  return await get(`authed/learner/courses/${courseId}/modules/${moduleId}`);
};

export const markLearnerViewed = async ({
  courseId,
  moduleId,
}: {
  courseId: string;
  moduleId: string;
}) => {
  return await post(`authed/learner/courses/${courseId}/modules/${moduleId}/viewed`);
};

export const setLearnerCompleted = async ({
  courseId,
  moduleId,
}: {
  courseId: string;
  moduleId: string;
}) => {
  return await post(`authed/learner/courses/${courseId}/modules/${moduleId}/completed`);
};

export const resetLearnerModule = async ({
  courseId,
  moduleId,
}: {
  courseId: string;
  moduleId: string;
}) => {
  return await del(`authed/learner/courses/${courseId}/modules/${moduleId}/reset`);
};

export const updateLearnerStatus = async ({
  courseId,
  moduleId,
  lastActive,
  eventType,
}: {
  courseId: string;
  moduleId: string;
  lastActive: Date;
  eventType?: string;
}) => {
  return await post(`authed/learner/courses/${courseId}/modules/${moduleId}/status`, {
    lastActive,
    eventType,
  });
};

export const submitLearnerFeedback = async (formData: FormData) => {
  return await post(`authed/learner/feedback`, formData);
};

/** Admin routes */
export const getCourses = async () => {
  return await get(`admin/courses`);
};

export const getCourse = async (courseId: string) => {
  return await get(`admin/courses/${courseId}`);
};

export const getCourseReport = async (courseId: string, userId: string) => {
  return await get(`admin/courses/${courseId}/user/${userId}/report`, {
    responseType: 'blob',
  });
};

export const addCourse = async (formData: FormData) => {
  return await post(`admin/courses`, formData);
};

export const updateCourse = async (courseId: string, formData: FormData) => {
  return await put(`admin/courses/${courseId}`, formData);
};

export const getModule = async ({ courseId, moduleId }: { courseId: string; moduleId: string }) => {
  return await get(`admin/courses/${courseId}/modules/${moduleId}`);
};

export const addModule = async ({
  courseId,
  formData,
}: {
  courseId: string;
  formData: FormData;
}) => {
  return await post(`admin/courses/${courseId}/modules`, formData);
};

export const updateModule = async ({
  courseId,
  moduleId,
  formData,
}: {
  courseId: string;
  moduleId: string;
  formData: FormData;
}) => {
  return await put(`admin/courses/${courseId}/modules/${moduleId}`, formData);
};

export const queueTranscription = async (courseId: string, moduleId: string) => {
  return await put(`admin/courses/${courseId}/modules/${moduleId}/transcribe`);
};

export const deleteModule = async ({
  courseId,
  moduleId,
}: {
  courseId: string;
  moduleId: string;
}) => {
  return await del(`admin/courses/${courseId}/modules/${moduleId}`);
};

export const deleteCourse = async ({ courseId }: { courseId: string }) => {
  return await del(`admin/courses/${courseId}`);
};

export const getAuthorModules = async () => {
  return await get(`admin/author/modules`);
};

// Teams
export const getTeams = async () => {
  return await get(`admin/teams`);
};

export const getTeam = async (teamId: string) => {
  return await get(`admin/teams/${teamId}`);
};

export const addTeam = async (formData: FormData) => {
  return await post(`admin/teams`, formData);
};

export const updateTeamSettings = async (teamId: string, formData: FormData) => {
  return await put(`admin/teams/${teamId}/settings`, formData);
};

export const updateTeamCourseAssignments = async (teamId: string, data: any) => {
  return await put(`admin/teams/${teamId}/courses`, data);
};

export const updateTeamLearnerAssignments = async (teamId: string, data: any) => {
  return await put(`admin/teams/${teamId}/learners`, data);
};

export const updateTeamManagerAssignments = async (teamId: string, data: any) => {
  return await put(`admin/teams/${teamId}/managers`, data);
};

export const bulkUpdateNotification = async (teamId: string, users: IUser[]) => {
  return await put(`admin/teams/${teamId}/notification`, { users });
};

export const updateManagerLearnerAssignments = async (
  teamId: string,
  userId: string,
  learnerAssignments: IManagerLearnerAssignment[],
) => {
  return await put(`admin/teams/${teamId}/managers/${userId}/learnerAssignments`, {
    learnerAssignments,
  });
};

export const sendTeamWelcomeEmail = async (
  teamId: string,
  includeAll: boolean,
  includeSubteams: boolean,
) => {
  return await post(`admin/teams/${teamId}/welcome?all=${includeAll}&subteams=${includeSubteams}`);
};

export const deleteTeam = async ({ teamId }: { teamId: string }) => {
  return await del(`admin/teams/${teamId}`);
};

export const deactivateTeam = async ({ teamId }: { teamId: string }) => {
  return await put(`admin/teams/${teamId}/deactivate`);
};

export const resetTeamCourseProgress = async (teamId: string) => {
  return await put(`admin/teams/${teamId}/reset`, undefined, { timeout: 600000 });
};

// Users
export const getUsers = async () => {
  return await get(`admin/users`);
};

export const getUser = async (userId: string) => {
  return await get(`admin/users/${userId}`);
};

export const addUser = async (formData: FormData) => {
  return await post(`admin/users`, formData);
};

export const updateUser = async (userId: string, formData: FormData) => {
  return await put(`admin/users/${userId}`, formData);
};

export const deleteUser = async (userId: string) => {
  return await del(`admin/users/${userId}`);
};

export const expirePassword = async (userId: string) => {
  return await put(`admin/users/${userId}/expire`);
};

export const sendUserWelcomeEmail = async (userId: string) => {
  return await post(`admin/users/${userId}/welcome`);
};

export const bulkUploadUsers = async (users: IUser[]) => {
  return await post(`admin/users/bulk`, { users });
};

export const bulkEditUsers = async (users: IUser[]) => {
  return await put(`admin/users/bulk`, { users });
};

export const getOnlineData = async () => {
  return await get(`admin/users/online`);
};

export const getCourseAssignmentDetails = async (
  userId: string,
  courseId: string,
  teamId?: string | null,
) => {
  return await get(`admin/users/${userId}/courses/${courseId}${teamId ? `?teamId=${teamId}` : ''}`);
};

export const getCourseProgressDetails = async (userId: string, courseId: string) => {
  return await get(`admin/users/${userId}/courses/${courseId}`);
};

export const resetCourseProgress = async (userId: string, courseId: string) => {
  return await put(`admin/users/${userId}/courses/${courseId}/reset`);
};

export const resetAllProgress = async (userId: string) => {
  return await put(`admin/users/${userId}/reset`);
};

export const updateCourseProgressDetails = async ({
  userId,
  courseId,
  details,
}: {
  userId: string;
  courseId: string;
  details: any;
}) => {
  return await put(`admin/users/${userId}/courses/${courseId}`, details);
};

// Settings
export const getSettings = async () => {
  return await get(`admin/settings`);
};

export const forceMaintenance = async () => {
  return await post(`admin/settings/force`);
};

export const updateSettings = async (formData: FormData) => {
  return await put(`admin/settings`, formData);
};

export const reloadSSOConfigs = async () => {
  return await put(`admin/settings/ssoConfigs`);
};

// Logs
export const getLogs = async (logType: string) => {
  return await get(`admin/logs/${logType}`);
};

// Admin Reports
export const getCLEReportData = async (from: Date, to: Date) => {
  return await get(`admin/reports/cle?from=${from.toISOString()}&to=${to.toISOString()}`, {
    timeout: 600000,
  });
};

export const getGeneralReportData = async (teamIds: string[]) => {
  return await post(
    `admin/reports/general`,
    {
      teamIds,
    },
    {
      timeout: 600000,
    },
  );
};

/** Reporting routes */
export const getTeamManagerReportData = async () => {
  return await get(`reporting/report`, { timeout: 600000 });
};

/** Platform specific routes */
export const getPlatformSettings = async () => {
  return await AXIOS.get(`platform/settings`);
};

export const updatePlatformSettings = async (formData: FormData) => {
  return await AXIOS.put(`platform/settings`, formData);
};
